import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import DOMPurify from "dompurify";

import NavBar from "../components/common/navBar";
import Footer from "../components/common/footer";
import Logo from "../components/common/logo";
import SEOHead from "../components/common/SEOHead";

import "./styles/admin.css";

const API_BASE = "https://api.northwestwind.org";
const FRONTEND_PASSWORD = "Macarena1990";
const STORAGE_KEY = "northwestwind-admin-auth";

const EMPTY_ARTICLE = {
	articleId: "",
	title: "",
	date: new Date().toISOString().slice(0, 10),
	description: "",
	keywords: "",
	body: "<p></p>",
};

const TOOLBAR_ACTIONS = [
	{ label: "P", action: "paragraph" },
	{ label: "H2", action: "heading" },
	{ label: "B", action: "bold" },
	{ label: "UL", action: "unorderedList" },
	{ label: "OL", action: "orderedList" },
	{ label: "Code", action: "code" },
];

const createArticleId = () => {
	if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
		return crypto.randomUUID();
	}

	return `article-${Date.now()}`;
};

const extractResponseData = async (response) => {
	const text = await response.text();
	if (!text) {
		return null;
	}

	try {
		const parsed = JSON.parse(text);
		if (typeof parsed === "string") {
			try {
				return JSON.parse(parsed);
			} catch (_error) {
				return parsed;
			}
		}
		if (
			parsed &&
			typeof parsed === "object" &&
			"body" in parsed &&
			!Array.isArray(parsed) &&
			Object.keys(parsed).length <= 3 &&
			("statusCode" in parsed || "headers" in parsed)
		) {
			try {
				return JSON.parse(parsed.body);
			} catch (_error) {
				return parsed.body;
			}
		}
		return parsed;
	} catch (_error) {
		return text;
	}
};

const normalizeArticle = (article) => ({
	articleId: article.articleId || article.id || "",
	title: article.title || "",
	date: article.date || "",
	description: article.description || "",
	keywords: Array.isArray(article.keywords)
		? article.keywords.join(", ")
		: article.keywords || "",
	body: article.body || "<p></p>",
});

const buildPayload = (draft) => ({
	articleId: draft.articleId.trim(),
	title: draft.title.trim(),
	date: draft.date.trim(),
	description: draft.description.trim(),
	keywords: draft.keywords
		.split(",")
		.map((keyword) => keyword.trim())
		.filter(Boolean),
	body: draft.body,
});

const isVisibleArticle = (article) => {
	const year = Number(String(article?.date || "").slice(0, 4));
	return Number.isFinite(year) && year > 2024;
};

const inferImageUrl = (payload) => {
	if (!payload) {
		return "";
	}

	if (typeof payload === "string") {
		return payload.startsWith("http") ? payload : "";
	}

	const directUrl =
		payload.url ||
		payload.imageUrl ||
		payload.location ||
		payload.uploadUrl ||
		payload.fileUrl;

	if (typeof directUrl === "string" && directUrl.startsWith("http")) {
		return directUrl;
	}

	if (typeof payload.body === "string" && payload.body.startsWith("http")) {
		return payload.body;
	}

	return "";
};

const Admin = () => {
	const editorRef = useRef(null);
	const imageInputRef = useRef(null);

	const [isAuthenticated, setIsAuthenticated] = useState(false);
	const [loginForm, setLoginForm] = useState({ apiKey: "", password: "" });
	const [authError, setAuthError] = useState("");

	const [articles, setArticles] = useState([]);
	const [selectedId, setSelectedId] = useState("");
	const [draft, setDraft] = useState(EMPTY_ARTICLE);
	const [statusMessage, setStatusMessage] = useState("");
	const [errorMessage, setErrorMessage] = useState("");
	const [isBusy, setIsBusy] = useState(false);
	const [search, setSearch] = useState("");

	useEffect(() => {
		window.scrollTo(0, 0);

		const stored = sessionStorage.getItem(STORAGE_KEY);
		if (!stored) {
			return;
		}

		try {
			const parsed = JSON.parse(stored);
			if (parsed?.apiKey) {
				setLoginForm({ apiKey: parsed.apiKey, password: "" });
				setIsAuthenticated(true);
			}
		} catch (_error) {
			sessionStorage.removeItem(STORAGE_KEY);
		}
	}, []);

	const filteredArticles = useMemo(() => {
		const query = search.trim().toLowerCase();
		if (!query) {
			return articles;
		}

		return articles.filter((article) =>
			[article.title, article.description, article.articleId]
				.filter(Boolean)
				.some((value) => value.toLowerCase().includes(query))
		);
	}, [articles, search]);

	const apiKey = loginForm.apiKey.trim();

	const clearMessages = () => {
		setStatusMessage("");
		setErrorMessage("");
	};

	const fetchArticles = useCallback(async () => {
		setIsBusy(true);
		clearMessages();

		try {
			const response = await fetch(`${API_BASE}/articles`);
			if (!response.ok) {
				throw new Error(`Unable to retrieve articles (${response.status})`);
			}

			const data = await extractResponseData(response);
			const list = Array.isArray(data) ? data : Array.isArray(data?.articles) ? data.articles : [];
			setArticles(
				[...list]
					.filter(isVisibleArticle)
					.sort((a, b) => String(b.date || "").localeCompare(String(a.date || "")))
			);
		} catch (error) {
			setErrorMessage(error.message);
		} finally {
			setIsBusy(false);
		}
	}, []);

	useEffect(() => {
		if (isAuthenticated) {
			fetchArticles();
		}
	}, [fetchArticles, isAuthenticated]);

	const loadArticle = async (articleId) => {
		setIsBusy(true);
		clearMessages();
		const listArticle = articles.find((article) => article.articleId === articleId);
		if (listArticle) {
			const normalizedListArticle = normalizeArticle(listArticle);
			setDraft(normalizedListArticle);
			setSelectedId(normalizedListArticle.articleId);
		}

		try {
			const response = await fetch(`${API_BASE}/articles/${articleId}`);
			if (!response.ok) {
				throw new Error(`Unable to load article (${response.status})`);
			}

			const data = await extractResponseData(response);
			const normalized = normalizeArticle(data || {});
			setDraft(normalized);
			setSelectedId(normalized.articleId);
		} catch (error) {
			setErrorMessage(error.message);
		} finally {
			setIsBusy(false);
		}
	};

	const handleLogin = (event) => {
		event.preventDefault();
		clearMessages();

		if (loginForm.password !== FRONTEND_PASSWORD) {
			setAuthError("The frontend password is not correct.");
			return;
		}

		if (!apiKey) {
			setAuthError("Enter the API key used for article management.");
			return;
		}

		sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ apiKey }));
		setAuthError("");
		setIsAuthenticated(true);
	};

	const handleLogout = () => {
		sessionStorage.removeItem(STORAGE_KEY);
		setIsAuthenticated(false);
		setLoginForm({ apiKey: "", password: "" });
		setArticles([]);
		setDraft(EMPTY_ARTICLE);
		setSelectedId("");
		clearMessages();
	};

	const updateDraft = (field, value) => {
		setDraft((current) => ({ ...current, [field]: value }));
	};

	const withApiHeaders = (includeJson = true) => {
		const headers = { "x-api-key": apiKey };
		if (includeJson) {
			headers["Content-Type"] = "application/json";
		}
		return headers;
	};

	const handleNewArticle = () => {
		clearMessages();
		setSelectedId("");
		setDraft({
			...EMPTY_ARTICLE,
			articleId: createArticleId(),
			date: new Date().toISOString().slice(0, 10),
		});
	};

	const saveArticle = async (mode) => {
		const articleId = draft.articleId.trim() || createArticleId();

		if (!draft.title.trim()) {
			setErrorMessage("`title` is required.");
			return;
		}

		if (!draft.body.trim()) {
			setErrorMessage("`body` is required.");
			return;
		}

		setIsBusy(true);
		clearMessages();

		try {
			const method = mode === "create" ? "POST" : "PUT";
			if (mode === "create" && !draft.articleId.trim()) {
				setDraft((current) => ({ ...current, articleId }));
			}
			const response = await fetch(`${API_BASE}/articles/${articleId}`, {
				method,
				headers: withApiHeaders(true),
				body: JSON.stringify(buildPayload({ ...draft, articleId })),
			});

			if (!response.ok) {
				throw new Error(`${mode === "create" ? "Create" : "Update"} failed (${response.status})`);
			}

			await extractResponseData(response);
			setStatusMessage(mode === "create" ? "Article created." : "Article updated.");
			await fetchArticles();
			await loadArticle(articleId);
		} catch (error) {
			setErrorMessage(error.message);
		} finally {
			setIsBusy(false);
		}
	};

	const deleteArticle = async () => {
		if (!draft.articleId.trim()) {
			setErrorMessage("Select an article first.");
			return;
		}

		if (!window.confirm(`Delete article ${draft.articleId}?`)) {
			return;
		}

		setIsBusy(true);
		clearMessages();

		try {
			const response = await fetch(`${API_BASE}/articles/${draft.articleId}`, {
				method: "DELETE",
				headers: withApiHeaders(false),
			});

			if (!response.ok) {
				throw new Error(`Delete failed (${response.status})`);
			}

			await extractResponseData(response);
			setStatusMessage("Article deleted.");
			setDraft(EMPTY_ARTICLE);
			setSelectedId("");
			await fetchArticles();
		} catch (error) {
			setErrorMessage(error.message);
		} finally {
			setIsBusy(false);
		}
	};

	const applyMarkup = (type) => {
		const editor = editorRef.current;
		if (!editor) {
			return;
		}

		const start = editor.selectionStart;
		const end = editor.selectionEnd;
		const selectedText = draft.body.slice(start, end) || "text";

		const snippets = {
			paragraph: `<p>${selectedText}</p>`,
			heading: `<h2>${selectedText}</h2>`,
			bold: `<strong>${selectedText}</strong>`,
			unorderedList: `<ul>\n  <li>${selectedText}</li>\n</ul>`,
			orderedList: `<ol>\n  <li>${selectedText}</li>\n</ol>`,
			code: `<pre class="language-javascript"><code>${selectedText}</code></pre>`,
		};

		const snippet = snippets[type];
		const nextBody = `${draft.body.slice(0, start)}${snippet}${draft.body.slice(end)}`;
		updateDraft("body", nextBody);

		requestAnimationFrame(() => {
			editor.focus();
			const caret = start + snippet.length;
			editor.setSelectionRange(caret, caret);
		});
	};

	const insertAtCursor = (snippet) => {
		const editor = editorRef.current;
		if (!editor) {
			updateDraft("body", `${draft.body}\n${snippet}`);
			return;
		}

		const start = editor.selectionStart;
		const end = editor.selectionEnd;
		const nextBody = `${draft.body.slice(0, start)}${snippet}${draft.body.slice(end)}`;
		updateDraft("body", nextBody);

		requestAnimationFrame(() => {
			editor.focus();
			const caret = start + snippet.length;
			editor.setSelectionRange(caret, caret);
		});
	};

	const readFileAsDataUrl = (file) =>
		new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.onload = () => resolve(reader.result);
			reader.onerror = () => reject(new Error("Unable to read image file."));
			reader.readAsDataURL(file);
		});

	const uploadImage = async (event) => {
		const file = event.target.files?.[0];
		if (!file) {
			return;
		}

		setIsBusy(true);
		clearMessages();

		try {
			const base64Image = await readFileAsDataUrl(file);

			const response = await fetch(`${API_BASE}/articles/images`, {
				method: "POST",
				headers: {
					"x-api-key": apiKey,
					"Content-Type": "application/json",
				},
				body: JSON.stringify({
					image: base64Image,
				}),
			});

			if (!response.ok) {
				throw new Error(`Image upload failed (${response.status})`);
			}

			const payload = await extractResponseData(response);
			const imageUrl = inferImageUrl(payload);
			if (!imageUrl) {
				throw new Error("Image uploaded, but the API did not return a usable URL.");
			}

			insertAtCursor(`\n<p><img src="${imageUrl}" alt="${file.name}" width="500"></p>\n`);
			setStatusMessage("Image uploaded and inserted into the article body.");
		} catch (error) {
			setErrorMessage(error.message);
		} finally {
			event.target.value = "";
			setIsBusy(false);
		}
	};

	const previewHtml = useMemo(() => DOMPurify.sanitize(draft.body || ""), [draft.body]);

	if (!isAuthenticated) {
		return (
			<React.Fragment>
				<SEOHead
					title="Admin"
					description="Private North West Wind article administration area."
					path="/admin"
					noindex
				/>

				<div className="page-content">
					<NavBar />
					<div className="content-wrapper">
						<div className="admin-logo-container">
							<div className="admin-logo">
								<Logo width={46} />
							</div>
						</div>
						<div className="admin-auth-container">
							<div className="title admin-title">Admin</div>
							<div className="subtitle admin-subtitle">
								Enter the article-management API key and the frontend password to open the editor panel.
							</div>
							<form className="admin-auth-form" onSubmit={handleLogin}>
								<label className="admin-field">
									<span>API Key</span>
									<input
										type="password"
										value={loginForm.apiKey}
										onChange={(event) =>
											setLoginForm((current) => ({
												...current,
												apiKey: event.target.value,
											}))
										}
										placeholder="x-api-key"
									/>
								</label>
								<label className="admin-field">
									<span>Frontend Password</span>
									<input
										type="password"
										value={loginForm.password}
										onChange={(event) =>
											setLoginForm((current) => ({
												...current,
												password: event.target.value,
											}))
										}
										placeholder="Frontend password"
									/>
								</label>
								{authError ? <div className="admin-error">{authError}</div> : null}
								<button type="submit" className="admin-primary-button">
									Open Admin
								</button>
							</form>
						</div>
						<div className="page-footer">
							<Footer />
						</div>
					</div>
				</div>
			</React.Fragment>
		);
	}

	return (
		<React.Fragment>
			<SEOHead
				title="Admin"
				description="Private North West Wind article administration area."
				path="/admin"
				noindex
			/>

			<div className="page-content">
				<NavBar />
				<div className="content-wrapper">
					<div className="admin-logo-container">
						<div className="admin-logo">
							<Logo width={46} />
						</div>
					</div>

					<div className="admin-page">
						<div className="admin-header">
							<div>
								<div className="title admin-title">Article Admin</div>
								<div className="subtitle admin-subtitle">
									Retrieve, create, edit, preview, and delete articles. The API key entered above is used for protected operations.
								</div>
							</div>
							<div className="admin-header-actions">
								<button className="admin-secondary-button" onClick={fetchArticles} type="button">
									Refresh
								</button>
								<button className="admin-secondary-button" onClick={handleLogout} type="button">
									Log out
								</button>
							</div>
						</div>

						{statusMessage ? <div className="admin-status admin-success">{statusMessage}</div> : null}
						{errorMessage ? <div className="admin-status admin-error">{errorMessage}</div> : null}

						<div className="admin-layout">
							<aside className="admin-sidebar">
								<div className="admin-sidebar-header">
									<h3>Articles</h3>
									<button className="admin-primary-button" type="button" onClick={handleNewArticle}>
										New
									</button>
								</div>
								<input
									className="admin-search"
									type="text"
									placeholder="Search articles"
									value={search}
									onChange={(event) => setSearch(event.target.value)}
								/>
								<div className="admin-article-list">
									{filteredArticles.map((article) => (
										<button
											key={article.articleId}
											type="button"
											className={`admin-article-list-item${
												selectedId === article.articleId ? " active" : ""
											}`}
											onClick={() => loadArticle(article.articleId)}
										>
											<div className="admin-article-list-title">{article.title}</div>
											<div className="admin-article-list-meta">
												{article.date || "No date"} · {article.articleId}
											</div>
										</button>
									))}
									{!filteredArticles.length ? (
										<div className="admin-empty-state">
											{isBusy ? "Loading..." : "No articles found."}
										</div>
									) : null}
								</div>
							</aside>

							<section className="admin-editor-panel">
								<div className="admin-form-grid">
									<label className="admin-field">
										<span>Article ID</span>
										<input
											type="text"
											value={draft.articleId}
											onChange={(event) => updateDraft("articleId", event.target.value)}
										/>
									</label>
									<label className="admin-field">
										<span>Date</span>
										<input
											type="date"
											value={draft.date}
											onChange={(event) => updateDraft("date", event.target.value)}
										/>
									</label>
									<label className="admin-field admin-field-full">
										<span>Title</span>
										<input
											type="text"
											value={draft.title}
											onChange={(event) => updateDraft("title", event.target.value)}
										/>
									</label>
									<label className="admin-field admin-field-full">
										<span>Description</span>
										<textarea
											rows={3}
											value={draft.description}
											onChange={(event) => updateDraft("description", event.target.value)}
										/>
									</label>
									<label className="admin-field admin-field-full">
										<span>Keywords</span>
										<input
											type="text"
											value={draft.keywords}
											onChange={(event) => updateDraft("keywords", event.target.value)}
											placeholder="keyword one, keyword two"
										/>
									</label>
								</div>

								<div className="admin-editor-shell">
									<div className="admin-editor-toolbar">
										{TOOLBAR_ACTIONS.map((item) => (
											<button
												key={item.action}
												type="button"
												className="admin-toolbar-button"
												onClick={() => applyMarkup(item.action)}
											>
												{item.label}
											</button>
										))}
										<button
											type="button"
											className="admin-toolbar-button"
											onClick={() => imageInputRef.current?.click()}
										>
											Image
										</button>
										<input
											ref={imageInputRef}
											type="file"
											accept="image/png,image/jpeg,image/jpg"
											className="admin-hidden-input"
											onChange={uploadImage}
										/>
									</div>

									<div className="admin-editor-columns">
										<div className="admin-editor-column">
											<div className="admin-panel-title">HTML Editor</div>
											<textarea
												ref={editorRef}
												className="admin-body-editor"
												value={draft.body}
												onChange={(event) => updateDraft("body", event.target.value)}
											/>
										</div>

										<div className="admin-editor-column">
											<div className="admin-panel-title">Preview</div>
											<div
												className="admin-preview"
												dangerouslySetInnerHTML={{ __html: previewHtml }}
											/>
										</div>
									</div>
								</div>

								<div className="admin-action-row">
									<button
										className="admin-primary-button"
										type="button"
										onClick={() => saveArticle("create")}
										disabled={isBusy}
									>
										Create Article
									</button>
									<button
										className="admin-secondary-button"
										type="button"
										onClick={() => saveArticle("update")}
										disabled={isBusy}
									>
										Update Article
									</button>
									<button
										className="admin-danger-button"
										type="button"
										onClick={deleteArticle}
										disabled={isBusy}
									>
										Delete Article
									</button>
								</div>
							</section>
						</div>
					</div>

					<div className="page-footer">
						<Footer />
					</div>
				</div>
			</div>
		</React.Fragment>
	);
};

export default Admin;
